import { FormikErrors, FormikTouched, useFormik } from "formik";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";

import { Container, Dialog, Icon, Pressable, useToast } from "../components";
import { PulseLoader } from "../components/pulse_loader";
import { useConfig } from "../core/config";
import { Location, Space } from "../data";
import { PartnerOffering, TemplateName } from "../data/partner_offering";
import { TranzilaAdditionalParams } from "../data/resource_integration";
import { useQueryString } from "../hooks";
import { FBT, useI18n } from "../lib/i18n/i18n_provider";
import {
  isCountryIsrael,
  isValidIsraeliTaxID,
} from "../lib/validation/israeli";
import { isEmail } from "../lib/validation/string";
import {
  AddOnType,
  BookingType,
  Order,
  OrderableType,
  OrderItemInput,
  OrderStatus,
  Quote,
  useCreateOrder,
  useOrderQueryCallback,
  usePartnerCatalogQueryMaybe,
  useSpaceBookingPolicyQuery,
  useSpaceOfferingQuery,
  useUpdateOrder,
} from "./api";
import { BackButton } from "./back_button";
import { Content } from "./content";
import { Divider } from "./divider";
import { Footer } from "./footer";
import { Heading } from "./heading";
import { Hidden } from "./hidden";
import { LoadingOverlay } from "./loading_overlay";
import { LocaleOption } from "./locale_data";
import { MarketingConsent } from "./marketing_consent";
import { OfferingFooterMobile } from "./offering_footer_mobile";
import { OfferingHeader } from "./offering_header";
import { useSyncQueryString } from "./offering_helpers";
import { OrderParams } from "./order_params";
import { PageLayout } from "./page_layout";
import { QuoteView } from "./quote_view";
import { Spacer } from "./spacer";
import {
  BillingDetails,
  OfferingParams,
  GuestDetails,
  PaymentCTA,
  Promotion,
  PaymentCTAWithTerms,
} from "./template_offering_checkout_common";
import { Text } from "./text";
import { useUTMParams } from "./utm";
import { tranzilaIframeURL } from "../core/tranzila";
import { isHourlyValue } from "@/offering_v2/components/date-time-picker/date_time_picker";
import { APIError, useGetLightPartnerOffering } from "@/data/api";
import { useAnalytics } from "@/lib/analytics/analytics";
import {
  getStartDateFromOrderParams,
  getEndDateFromOrderParams,
} from "./order_params_utils";
import { usePaymentTimer } from "@/offering_v2/hooks/use_payment_timer";
import { ExpiredPaymentWarning } from "@/offering_v2/components/expired_payment_warning";

export interface TemplateOfferingCheckoutTranzilaBaseQueryParams {
  locationID?: string;
  startDate?: string;
  endDate?: string;
  addOnID?: string;
}

export interface TemplateOfferingCheckoutTranzilaBaseProps {
  // UI
  favicon: string;
  homepageURL: string;
  termsURL?: string;
  hubspotForm?: React.ReactNode;
  partnerOfferingSlug: string;
  showLanguageSelector: boolean;
  localeOptions?: LocaleOption[];
  showTalkWithUsPrompt?: boolean;
  disabledCheckout: boolean;
  displayAccessibility?: string;

  // Data
  quote: Quote | undefined;
  space: Space | undefined;
  location: Location | undefined;
  partnerOffering: PartnerOffering;
  askForTax: boolean;
  askForBookingReason?: boolean;

  // Tranzila
  offeringName: string;
  tranzilaId?: string;
  requirePayment: boolean;
  order: Order | undefined;
  tranzilaAdditionalParams?: TranzilaAdditionalParams;
  onCloseTranzila: () => void;
  onPaymentSuccess: () => void;

  // Checkout
  orderParams: OrderParams;
  orderParamsError: string | undefined;
  form: {
    values: CheckoutTranzilaInput;
    errors: FormikErrors<CheckoutTranzilaInput>;
    touched: FormikTouched<CheckoutTranzilaInput>;
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => any;
    setFieldError: (field: string, value: string | undefined) => void;
    submit: () => Promise<void>;
    submitting: boolean;
  };
  handleChangePromoCode: (promoCode: string) => void;
  validateOrderParams: () => void;
  onPressBack: () => void;
  onChangeEmail: (email?: string) => void;

  mobileCheckoutBox: React.ReactNode;
  desktopCheckoutBox: React.ReactNode;
}

export function TemplateOfferingCheckoutTranzilaBase(
  props: TemplateOfferingCheckoutTranzilaBaseProps
) {
  const {
    offeringName,
    partnerOfferingSlug,
    hubspotForm,
    homepageURL,
    favicon,
    termsURL,
    displayAccessibility,

    tranzilaId,
    requirePayment,
    order,
    tranzilaAdditionalParams,
    onCloseTranzila,

    quote,
    space,
    location,
    partnerOffering,
    askForTax,
    askForBookingReason,

    orderParams,
    orderParamsError,
    form,
    handleChangePromoCode,
    validateOrderParams,
    onPaymentSuccess,
    onPressBack,
    onChangeEmail,

    showLanguageSelector,
    localeOptions,
    showTalkWithUsPrompt,
    disabledCheckout,
    mobileCheckoutBox,
    desktopCheckoutBox,
  } = props;
  const { fbt } = useI18n();
  const { values, errors, setFieldValue, submit, submitting, touched } = form;
  const config = useConfig();
  const toast = useToast();
  const { locale } = useI18n();
  const getOrder = useOrderQueryCallback();
  const completedRef = useRef(false);
  const [polling, setPolling] = useState(false);
  useSyncQueryString(orderParams);
  const queryString = useQueryString<OfferingParams>();
  const partnerCatalog = usePartnerCatalogQueryMaybe(
    partnerOffering.partner.partnerSlug,
    queryString.fromCatalog
  );
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoading, setIframeLoading] = useState(false);
  const tranzilaOpen = !!order && requirePayment;
  const { isExpired, resetTimer } = usePaymentTimer(tranzilaOpen);

  const handlePaymentSuccess = useCallback(() => {
    onPaymentSuccess();
  }, [onPaymentSuccess]);
  const addOnOrderItem = quote?.orderItems.find(
    (o) => o.type === OrderableType.AddOn
  );
  const promotableType = addOnOrderItem ? "SpaceOffering" : "Space";
  const spaceOffering = useSpaceOfferingQuery(
    orderParams.spaceID,
    addOnOrderItem?.type === OrderableType.AddOn
      ? addOnOrderItem?.details.addOnID
      : undefined
  );
  const promotableID =
    addOnOrderItem?.type === OrderableType.AddOn
      ? spaceOffering?.id
      : orderParams.spaceID;
  const { data: spaceBookingPolicy } = useSpaceBookingPolicyQuery(
    space?.id,
    getStartDateFromOrderParams(orderParams),
    getEndDateFromOrderParams(orderParams)
  );

  useEffect(() => {
    function onLoaded() {
      setIframeLoading(false);
    }

    // no need to setup payment when there is no order or order does not require payment
    // for example: when user apply 100% promotion code and the price is $0, ...
    if (!order || !requirePayment) {
      return;
    }

    const iframe = iframeRef.current;

    iframe?.addEventListener("load", onLoaded);

    return () => {
      iframe?.removeEventListener("load", onLoaded);
    };
  });

  useEffect(() => {
    // if order requires payment, let iframe events handle after payment is done
    if (order && requirePayment) {
      return;
    }

    // if no payment is require, confirm will go directly to confirmed order page
    handlePaymentSuccess();
  }, [order, requirePayment, handlePaymentSuccess]);

  const handleStartPollingOrder = useCallback(async () => {
    await new Promise<void>((resolve) => {
      let count = 0;

      // Start polling
      const interval = setInterval(async () => {
        // poll for maximum 60 seconds
        if (order && count < 60) {
          const polledOrder = await getOrder(order.id);
          count++;
          if (
            polledOrder.status === OrderStatus.Paid &&
            polledOrder.orderItems.length > 0
          ) {
            clearInterval(interval);
            // Even though we clear interval, async nature may still call this function
            // multiple times as response is returned
            if (completedRef.current === false) {
              resetTimer();
              handlePaymentSuccess();
              completedRef.current = true;
            }
            resolve();
          }
        } else {
          clearInterval(interval);
          toast.error({ message: "Could not get order confirmation" });
          setIframeLoading(false);
          onCloseTranzila();
          resolve();
        }
      }, 1000);
    });
  }, [
    order,
    getOrder,
    resetTimer,
    handlePaymentSuccess,
    toast,
    onCloseTranzila,
  ]);

  const handleIframeMessage = useCallback(
    async (e) => {
      if (e.data === "TRANZILA_PAYMENT_SUCCESS") {
        setPolling(true);
        await handleStartPollingOrder();
      }
      if (e.data === "TRANZILA_PAYMENT_FAILED") {
        onCloseTranzila();
        toast.error({ message: "Payment failed." });
      }
    },
    [handleStartPollingOrder, onCloseTranzila, toast]
  );

  useEffect(() => {
    window.addEventListener("message", handleIframeMessage);
    return () => {
      window.removeEventListener("message", handleIframeMessage);
    };
  }, [handleIframeMessage]);

  const handleSubmit = useCallback(async () => {
    // show loading state, submit order and wait for payment popup
    setIframeLoading(requirePayment);
    validateOrderParams();
    await submit();
  }, [validateOrderParams, submit, requirePayment]);

  return (
    <Container>
      <Helmet>
        <title>
          {offeringName} - {partnerOffering.partner.name}
        </title>
        <link href={favicon} rel="shortcut icon" type="image/x-icon" />
      </Helmet>
      <OfferingHeader
        partnerID={partnerOffering.partner.id}
        offeringID={partnerOfferingSlug}
        homepageURL={partnerOffering.partner.homepageUrl || homepageURL}
        logo={partnerOffering.partner.logoUrl}
        hubspotForm={hubspotForm}
        showLanguageSelector={showLanguageSelector}
        localeOptions={localeOptions}
        showTalkWithUsPrompt={showTalkWithUsPrompt}
      />
      <Content>
        <Container paddingHorizontal={16}>
          <Spacer size={16} />
          <BackButton onPress={onPressBack} />
          <Spacer size={16} />
        </Container>
        <Spacer size={24} />
      </Content>
      <PageLayout
        content={
          <Container paddingHorizontal={16}>
            <GuestDetails
              onChangeEmail={onChangeEmail}
              errors={errors}
              touched={touched}
              values={values}
              setFieldValue={setFieldValue}
              askForBookingReason={askForBookingReason}
            />

            <Spacer size={24} />
            <Hidden md>
              <Text weight="bold">{offeringName}</Text>
              <Spacer size={8} />
              <div className="template-offering-checkout-box-mobile">
                {mobileCheckoutBox}
              </div>
              <Spacer size={16} />
              <Divider />
              {quote && space && location && (
                <Fragment>
                  <QuoteView
                    orderParams={orderParams}
                    locationTimezone={location.timezone}
                    currency={space.currencyCode}
                    pricingInclusion={location.pricingInclusion}
                    quote={quote}
                  />
                  <Spacer size={16} />
                </Fragment>
              )}
              {orderParamsError && (
                <Fragment>
                  <Spacer size={24} />
                  <Text color="error">{orderParamsError}</Text>
                  <Spacer size={16} />
                </Fragment>
              )}
            </Hidden>
            {!partnerCatalog?.promoCode && (
              <Promotion
                partnerID={partnerOffering.partner.id}
                promotableType={promotableType}
                promotableID={promotableID}
                email={orderParams.email}
                dayPassDateRange={
                  orderParams.type === TemplateName.DayPass ||
                  orderParams.type === "DayOffice" ||
                  orderParams.type === "DailyAccess"
                    ? orderParams.dateRange
                    : undefined
                }
                timeRange={
                  orderParams.type === "MeetingRoom" &&
                  isHourlyValue(orderParams.dateRange)
                    ? orderParams.dateRange
                    : undefined
                }
                quantity={
                  orderParams.type === TemplateName.DayPass ||
                  orderParams.type === "DailyAccess"
                    ? orderParams.quantity
                    : undefined
                }
                onChangePromoCode={handleChangePromoCode}
              />
            )}
            {quote?.totalPrice !== 0 && (
              <Fragment>
                <Spacer size={24} />
                <BillingDetails
                  askForTax={askForTax}
                  errors={errors}
                  touched={touched}
                  values={values}
                  setFieldValue={setFieldValue}
                />
              </Fragment>
            )}
            <Spacer size={16} />
            {spaceBookingPolicy?.marketingConsent && (
              <Container padding={16}>
                <Spacer size={24} />
                <MarketingConsent
                  marketingConsent={spaceBookingPolicy?.marketingConsent}
                  values={values}
                  setFieldValue={setFieldValue}
                />
              </Container>
            )}
            {!spaceBookingPolicy?.marketingConsent && <Spacer size={24} />}
            {spaceBookingPolicy?.terms ? (
              <PaymentCTAWithTerms
                isPaymentRequired={quote?.totalPrice !== 0}
                terms={spaceBookingPolicy?.terms}
                errors={errors}
                values={values}
                setFieldValue={setFieldValue}
                onSubmit={handleSubmit}
                disabled={!values.termsChecked || disabledCheckout}
              />
            ) : (
              <PaymentCTA
                isPaymentRequired={quote?.totalPrice !== 0}
                termsURL={termsURL}
                onSubmit={handleSubmit}
                disabled={!values.termsChecked || disabledCheckout}
              />
            )}
          </Container>
        }
        rightSidebar={
          <Container>
            <Text weight="bold">{offeringName}</Text>
            <Spacer size={8} />
            {desktopCheckoutBox}
          </Container>
        }
      />
      <Dialog
        isOpen={tranzilaOpen}
        onRequestClose={() => {
          resetTimer();
          onCloseTranzila();
        }}
        override={{ body: { style: { height: isExpired ? undefined : 400 } } }}
      >
        {polling ? (
          <div className="template-offering-checkout-tranzila-dialog-body">
            <Container center padding={16}>
              <Text color="primary" align="center">
                {fbt("Redirecting you to confirmation page")}
              </Text>
              <Spacer size={40} />
              <PulseLoader />
            </Container>
          </div>
        ) : (
          <div className="template-offering-checkout-tranzila-dialog-body">
            <Container>
              <Pressable
                style={{ position: "absolute", left: 4 }}
                onPress={() => {
                  resetTimer();
                  onCloseTranzila();
                }}
              >
                <Icon name="x" />
              </Pressable>
              <Heading size="xl" align="center">
                {fbt("Payment")}
              </Heading>
            </Container>
            <Spacer size={24} />
            {iframeLoading && (
              <div style={{ position: "absolute", right: 0, left: 0, top: 40 }}>
                <Spacer size={40} />
                <Container center>
                  <PulseLoader />
                </Container>
              </div>
            )}
            {isExpired && <ExpiredPaymentWarning orderId={order?.id} />}
            {order && !isExpired && (
              <div className="template-offering-checkout-tranzila-iframe-container">
                <iframe
                  ref={iframeRef}
                  className="template-offering-checkout-tranzila-iframe"
                  title="template-offering-checkout-tranzila-iframe"
                  src={tranzilaIframeURL(
                    tranzilaId || "",
                    order,
                    config.tranzilaNotifyURLAddress,
                    locale,
                    tranzilaAdditionalParams
                  )}
                />
              </div>
            )}
          </div>
        )}
      </Dialog>
      <OfferingFooterMobile
        showLanguageSelector={showLanguageSelector}
        hubspotForm={hubspotForm}
        partnerID={partnerOffering.partner.id}
        offeringID={partnerOfferingSlug}
        showTalkWithUsPrompt={showTalkWithUsPrompt}
        localeOptions={localeOptions}
      />
      <Spacer size={20} />
      <Footer displayAccessibility={displayAccessibility} />
      {submitting && <LoadingOverlay />}
    </Container>
  );
}

export interface CheckoutTranzilaInput {
  fullName: string;
  companyName: string;
  phoneNumber: string;
  email: string;
  bookingReason?: string;
  billingStreetAddress: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingCountry: string;
  billingTax: string;
  marketingConsent: boolean;
  termsChecked: boolean;
}

export interface OrderInput {
  fullName: string;
  companyName: string;
  phoneNumber: string;
  email: string;
  bookingReason?: string;
  billingFullName?: string;
  billingPhoneNumber?: string;
  billingEmail?: string;
  billingStreetAddress: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingCountry: string;
  billingTax: string;
  marketingConsent: boolean;
  specialInstructions?: string;
  membershipNumber?: string;
}

interface UseSubmitOrderProps {
  partnerID: string;
  quoteID?: string;
  partnerOfferingID: string;
  requirePayment?: boolean;
  onError?: (msg: string, data?: any, order?: Order | null) => void;
}

const errorCodeToMessage = (partnerName: string, code: string): string => {
  const messages: { [code: string]: string } = {
    invalid_params:
      "There are missing parameters in the request. Please ensure all required fields are filled.",
    invalid_email: "Invalid email address. please enter a valid email.",
    invalid_quote:
      "There was an issue with the quote. Please refresh your browser and try again.",
    blacklist: "You are unable to purchase.",
    booking_unavailable:
      "The date and time for this meeting room is no longer available. Update your booking or find a different meeting room",
    unable_to_verify_availability: `The ${partnerName} booking system is currently down. Please try again by clicking confirm and pay.`,
    invalid_membership_number: "Invalid membership number.",
  };

  return messages[code];
};

export function useSubmitOrder(props: UseSubmitOrderProps) {
  const { quoteID, partnerID, partnerOfferingID, requirePayment, onError } =
    props;

  const analytics = useAnalytics();
  const partnerOffering = useGetLightPartnerOffering(partnerOfferingID);
  const { locale } = useI18n();
  const createOrder = useCreateOrder();
  const updateOrder = useUpdateOrder();
  const [existingOrder, setExistingOrder] = useState<Order | null>(null);
  const utmParams = useUTMParams();
  const partnerName = partnerOffering.partner.name;

  return useCallback(
    async (
      input: OrderInput,
      orderParams: OrderParams,
      legacy__details: {
        layoutID: string;
        attendees: number;
      }
    ) => {
      let order: Order | null = existingOrder;

      const orderItems: OrderItemInput[] = [];

      if (orderParams.type === "DayOffice") {
        if (
          !orderParams.dateRange?.startDate ||
          !orderParams.dateRange.endDate
        ) {
          onError?.("Date range is missing");
          return;
        }

        if (!orderParams.spaceID) {
          onError?.("Space id is missing");
          return;
        }
        orderItems.push({
          type: OrderableType.Booking,
          quantity: orderParams.quantity,
          details: {
            type: BookingType.Daily,
            spaceID: orderParams.spaceID,
            physicalSpaceID: orderParams.physicalSpaceID,
            startDate: orderParams.dateRange?.startDate,
            endDate: orderParams.dateRange?.endDate,
            legacy__details,
          },
        });
      } else if (orderParams.type === TemplateName.DayPass) {
        if (
          !orderParams.dateRange?.startDate ||
          !orderParams.dateRange.endDate
        ) {
          onError?.("Date range is missing");
          return;
        }

        if (!orderParams.spaceID) {
          onError?.("Space id is missing");
          return;
        }
        orderItems.push({
          type: OrderableType.Booking,
          quantity: orderParams.quantity,
          details: {
            type: BookingType.Daily,
            spaceID: orderParams.spaceID,
            startDate: orderParams.dateRange?.startDate,
            endDate: orderParams.dateRange?.endDate,
            legacy__details,
          },
        });
      } else if (orderParams.type === "DayPassWithCapacity") {
        if (
          !orderParams.dateRange?.startDate ||
          !orderParams.dateRange.endDate
        ) {
          onError?.("Date range is missing");
          return;
        }

        if (!orderParams.spaceID) {
          onError?.("Space id is missing");
          return;
        }
        orderItems.push({
          type: OrderableType.Booking,
          details: {
            type: BookingType.Daily,
            spaceID: orderParams.spaceID,
            startDate: orderParams.dateRange?.startDate,
            endDate: orderParams.dateRange?.endDate,
            legacy__details,
          },
        });
      } else if (orderParams.type === TemplateName.MultiPass) {
        if (!orderParams.addOnID || !orderParams.spaceOfferingID) {
          onError?.("Date range is missing");
          return;
        }

        if (!orderParams.spaceID) {
          onError?.("Space id is missing");
          return;
        }

        orderItems.push({
          type: OrderableType.AddOn,
          details: {
            type: AddOnType.MultiPass,
            spaceID: orderParams.spaceID,
            addOnID: orderParams.addOnID,
            spaceOfferingID: orderParams.spaceOfferingID,
          },
        });
      } else if (orderParams.type === TemplateName.MultiDayOffice) {
        if (!orderParams.addOnID || !orderParams.spaceOfferingID) {
          onError?.("Date range is missing");
          return;
        }

        if (!orderParams.spaceID) {
          onError?.("Space id is missing");
          return;
        }

        if (!orderParams.partnerOfferingLocationID) {
          onError?.("Location id is missing");
          return;
        }

        if (!orderParams.capacity) {
          onError?.("Capacity is missing");
          return;
        }

        orderItems.push({
          type: OrderableType.AddOn,
          details: {
            type: AddOnType.MultiPass,
            spaceID: orderParams.spaceID,
            addOnID: orderParams.addOnID,
            spaceOfferingID: orderParams.spaceOfferingID,
            capacity: orderParams.capacity,
          },
        });
      } else if (
        orderParams.type === "Hourly" ||
        orderParams.type === "MeetingRoom"
      ) {
        if (orderParams.type === "Hourly") {
          if (!orderParams.dateRange?.date) {
            onError?.("Date is missing");
            return;
          }
          if (!orderParams.dateRange?.startTime) {
            onError?.("Time is missing");
            return;
          }

          if (!orderParams.dateRange.endTime) {
            onError?.("Time range is missing");
            return;
          }
        }

        if (!orderParams.spaceID) {
          onError?.("Space id is missing");
          return;
        }

        if (!isHourlyValue(orderParams.dateRange)) {
          if (!orderParams.dateRange?.startDate) {
            onError?.("Date is missing");
            return;
          }

          if (!orderParams.dateRange?.endDate) {
            onError?.("End date is missing");
            return;
          }
        }

        if (
          isHourlyValue(orderParams.dateRange) &&
          orderParams.dateRange?.startTime &&
          orderParams.dateRange.endTime
        ) {
          orderItems.push({
            type: OrderableType.Booking,
            quantity: undefined,
            details: {
              type: BookingType.Hourly,
              spaceID: orderParams.spaceID,
              date: orderParams.dateRange.date,
              startTime: orderParams.dateRange.startTime,
              endTime: orderParams.dateRange.endTime,
              legacy__details,
            },
          });
        } else if (!isHourlyValue(orderParams.dateRange)) {
          orderItems.push({
            type: OrderableType.Booking,
            quantity: undefined,
            details: {
              type: BookingType.Hourly,
              spaceID: orderParams.spaceID,
              startDate: orderParams.dateRange.startDate,
              endDate: orderParams.dateRange.endDate,
              legacy__details,
            },
          });
        }
      } else if (orderParams.type === "EventSpace") {
        if (!orderParams.spaceID) {
          onError?.("Space id is missing");
          return;
        }

        if (isHourlyValue(orderParams.dateRange)) {
          if (!orderParams.dateRange?.date) {
            onError?.("Date is missing");
            return;
          }
          if (!orderParams.dateRange?.startTime) {
            onError?.("Time is missing");
            return;
          }

          if (!orderParams.dateRange.endTime) {
            onError?.("Time range is missing");
            return;
          }
        } else {
          if (!orderParams.dateRange?.startDate) {
            onError?.("Date is missing");
            return;
          }

          if (!orderParams.dateRange?.endDate) {
            onError?.("End date is missing");
            return;
          }
        }

        if (
          isHourlyValue(orderParams.dateRange) &&
          orderParams.dateRange?.startTime &&
          orderParams.dateRange.endTime
        ) {
          orderItems.push({
            type: OrderableType.Booking,
            quantity: undefined,
            details: {
              type: BookingType.Hourly,
              spaceID: orderParams.spaceID,
              date: orderParams.dateRange.date,
              startTime: orderParams.dateRange.startTime,
              endTime: orderParams.dateRange.endTime,
              legacy__details,
            },
          });
        } else if (!isHourlyValue(orderParams.dateRange)) {
          orderItems.push({
            type: OrderableType.Booking,
            quantity: undefined,
            details: {
              type: BookingType.Hourly,
              spaceID: orderParams.spaceID,
              startDate: orderParams.dateRange.startDate,
              endDate: orderParams.dateRange.endDate,
              legacy__details,
            },
          });
        }
      } else if (orderParams.type === "DailyAccess") {
        if (!orderParams.spaceID) {
          onError?.("Space id is missing");
          return;
        }

        if (!orderParams.addOnID || !orderParams.spaceOfferingID) {
          onError?.("Add on id is missing");
          return null;
        }

        if (orderParams.addOnID === "daypass") {
          if (
            !orderParams.dateRange?.startDate ||
            !orderParams.dateRange?.endDate
          ) {
            onError?.("Add on id is missing");
            return null;
          }

          orderItems.push({
            type: OrderableType.Booking,
            quantity: orderParams.quantity,
            details: {
              spaceID: orderParams.spaceID,
              type: BookingType.Daily,
              startDate: orderParams.dateRange.startDate,
              endDate: orderParams.dateRange.endDate,
              legacy__details: legacy__details || {
                attendees: 1,
                layoutID: "",
              },
            },
          });
        } else {
          orderItems.push({
            type: OrderableType.AddOn,
            details: {
              type: AddOnType.MultiPass,
              spaceID: orderParams.spaceID,
              addOnID: orderParams.addOnID,
              spaceOfferingID: orderParams.spaceOfferingID,
            },
          });
        }
      } else if (orderParams.type === "DailyOffice") {
        if (!orderParams.spaceID) {
          onError?.("Space id is missing");
          return;
        }

        if (!orderParams.addOnID || !orderParams.spaceOfferingID) {
          onError?.("Add on id is missing");
          return null;
        }

        if (orderParams.addOnID === "dayoffice") {
          if (
            !orderParams.dateRange?.startDate ||
            !orderParams.dateRange?.endDate
          ) {
            onError?.("Add on id is missing");
            return null;
          }
          orderItems.push({
            type: OrderableType.Booking,
            details: {
              type: BookingType.Daily,
              spaceID: orderParams.spaceID,
              startDate: orderParams.dateRange?.startDate,
              endDate: orderParams.dateRange?.endDate,
              legacy__details,
            },
          });
        } else {
          orderItems.push({
            type: OrderableType.AddOn,
            details: {
              type: AddOnType.MultiPass,
              spaceID: orderParams.spaceID,
              addOnID: orderParams.addOnID,
              spaceOfferingID: orderParams.spaceOfferingID,
            },
          });
        }
      } else if (orderParams.type === "Membership") {
        if (!orderParams.membershipID) {
          onError?.("MembershipId id is missing");
          return;
        }

        if (!orderParams.subscriptionInterval) {
          onError?.("Subscription interval is missing");
          return null;
        }

        if (!orderParams.subscriptionStartDate) {
          onError?.("Subscription start date is missing");
          return null;
        }

        orderItems.push({
          details: {
            membershipID: orderParams.membershipID,
            subscriptionInterval: orderParams.subscriptionInterval,
            subscriptionStartDate: orderParams.subscriptionStartDate,
          },
        });
      }

      if (order && requirePayment) {
        try {
          const params = {
            id: order.id,
            companyName: input.companyName,
            email: input.email,
            fullName: input.fullName,
            phoneNumber: input.phoneNumber,
            bookingReason: input.bookingReason,
            confirmWithoutPayment: false,
            locale: locale.baseLanguageTag,
            promoCode: orderParams.promoCode,
            marketingConsent: input.marketingConsent,
            membershipNumber: input.membershipNumber,
            orderItems,
          };

          analytics.event(
            "Checkout Update Order Request",
            {
              "Quote ID": quoteID,
              "Order ID": order.id,
              Params: params,
            },
            { onlyProviders: ["mixpanel", "customerio"] }
          );
          order = await updateOrder(params);
        } catch (error) {
          if (
            error instanceof APIError &&
            error.code &&
            errorCodeToMessage(partnerName, error.code)
          ) {
            onError?.(
              errorCodeToMessage(partnerName, error.code),
              {
                code: error.code,
                details: error.message,
              },
              order
            );
          } else {
            onError?.(
              "There was a problem submitting your order. Please try again.",
              error,
              order
            );
          }
          return;
        }
      } else {
        try {
          const params = {
            companyName: input.companyName,
            email: input.email,
            fullName: input.fullName,
            phoneNumber: input.phoneNumber,
            bookingReason: input.bookingReason,
            locale: locale.baseLanguageTag,
            promoCode: orderParams.promoCode,
            quoteID,
            partnerID,
            partnerOfferingID,
            confirmWithoutPayment: false,
            marketingConsent: input.marketingConsent,
            specialInstructions: input.specialInstructions,
            membershipNumber: input.membershipNumber,
            orderItems,
            billing: {
              fullName: input.billingFullName,
              phone: input.billingPhoneNumber,
              email: input.billingEmail,
              streetAddress: input.billingStreetAddress,
              city: input.billingCity,
              state: input.billingState,
              postalCode: input.billingPostalCode,
              country: input.billingCountry,
              tax: input.billingTax,
            },
            analytics: {
              ga: utmParams,
            },
          };

          analytics.event(
            "Checkout Create Order Request",
            {
              "Quote ID": quoteID,
              Params: params,
            },
            { onlyProviders: ["mixpanel", "customerio"] }
          );

          order = await createOrder(params);
          setExistingOrder(order);
        } catch (error) {
          if (
            error instanceof APIError &&
            error.code &&
            errorCodeToMessage(partnerName, error.code)
          ) {
            onError?.(
              errorCodeToMessage(partnerName, error.code),
              {
                code: error.code,
                details: error.message,
              },
              order
            );
          } else if (error instanceof APIError && !error.code) {
            // in case we do not know the error code, the error object was transfered to the else statement
            // this else if condition will catch it and we will not see minified error object name sent to sentry

            onError?.(
              error.message ||
                "There was a problem submitting your order. Please try again.",
              // do not send any extra variables for sentry since we do not have the code or sth.
              undefined,
              order
            );
          } else {
            // this can stay in case the error is not an APIError and we do not know the error type
            onError?.(
              "There was a problem submitting your order. Please try again.",
              error,
              order
            );
          }

          return;
        }
      }
      return order;
    },
    [
      existingOrder,
      requirePayment,
      onError,
      analytics,
      quoteID,
      updateOrder,
      locale.baseLanguageTag,
      partnerName,
      createOrder,
      partnerID,
      partnerOfferingID,
      utmParams,
    ]
  );
}

interface UseCheckoutTranzilaProps {
  requireBillingDetails: boolean;
  requirePayment: boolean;
  askForTax: boolean;
  askForBookingReason?: boolean;
  initialValues: CheckoutTranzilaInput;
  orderParams: OrderParams;
  partnerID: string;
  partnerOfferingID: string;
  legacy__details: {
    layoutID: string;
    attendees: number;
  };
  onOrderSubmitted: (order: Order) => void;
  onError?: (msg: string) => void;
}

export function useCheckoutTranzila(props: UseCheckoutTranzilaProps) {
  const {
    partnerID,
    partnerOfferingID,
    requirePayment,
    requireBillingDetails,
    askForTax,
    askForBookingReason,
    onOrderSubmitted,
    onError,
    orderParams,
    initialValues,
    legacy__details,
  } = props;
  const { fbt } = useI18n();
  const submitOrder = useSubmitOrder({
    partnerID,
    partnerOfferingID,
    requirePayment,
    onError,
  });

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setTouched,
    setFieldError,
    submitForm,
    isSubmitting,
  } = useFormik<CheckoutTranzilaInput>({
    initialValues,
    validate: (v) =>
      getCheckoutTranzilaErrors(
        v,
        requireBillingDetails,
        askForTax,
        requirePayment,
        fbt,
        askForBookingReason
      ),
    onSubmit: async (v) => {
      const order = await submitOrder(v, orderParams, legacy__details);
      if (onOrderSubmitted && order) {
        onOrderSubmitted(order);
      }
    },
  });

  const handleSetFieldValue = useCallback(
    (field: string, value: any) => {
      setTouched({
        ...touched,
        [field]: true,
      });
      setFieldValue(field, value);
    },
    [touched, setFieldValue, setTouched]
  );

  return {
    values,
    errors,
    touched,
    setFieldValue: handleSetFieldValue,
    setFieldError,
    submit: submitForm,
    submitting: isSubmitting,
  };
}

export function getCheckoutTranzilaErrors(
  values: CheckoutTranzilaInput,
  requireBillingDetails: boolean,
  askForTax: boolean,
  requirePayment: boolean,
  fbt: FBT,
  askForBookingReason?: boolean
) {
  const errors: { [field: string]: string } = {};

  if (!values.fullName) {
    errors.fullName = fbt("Please add your full name");
  }

  if (!values.termsChecked) {
    errors.termsChecked = fbt("Please agree to the terms and conditions");
  }

  if (requirePayment && !values.companyName.trim()) {
    errors.companyName = fbt("Please add your company name");
  }

  if (!values.phoneNumber) {
    errors.phoneNumber = fbt("Please add your phone number");
  } else if (values.phoneNumber.length < 5) {
    errors.phoneNumber = fbt("Please enter valid phone number");
  }

  if (!values.email) {
    errors.email = fbt("Please add your email");
  } else if (!isEmail(values.email)) {
    errors.email = fbt("Please enter valid email");
  }

  if (requireBillingDetails) {
    if (!values.billingStreetAddress) {
      errors.billingStreetAddress = fbt("Please add billing street address");
    }
    if (!values.billingCity) {
      errors.billingCity = fbt("Please add billing city");
    }
    if (!values.billingPostalCode) {
      errors.billingPostalCode = fbt("Please add postal code");
    }
    if (!values.billingCountry) {
      errors.billingCountry = fbt("Please add billing country");
    }
  }

  if (askForTax) {
    if (!values.billingTax) {
      errors.billingTax = fbt("Please add tax information");
    } else if (
      isCountryIsrael(values.billingCountry) &&
      !isValidIsraeliTaxID(values.billingTax)
    ) {
      errors.billingTax = fbt("Invalid Israel tax id");
    }
  }

  if (askForBookingReason && !values.bookingReason) {
    errors.bookingReason = fbt("missingBookingReasonError");
  }

  return errors;
}
